// src/pages/Settings/components/AppearanceSection.jsx
// STATUS: active | UPDATED: 2026-08-27
// LOG:
//   2026-08-27  New: light / dark / system appearance control.
// NEXT: write the choice to users.color_mode so it follows a salesperson from
//       the floor phone to the desk machine instead of living in one browser.
// Appearance. Three states, not two: System is the default and the honest one,
// because a man who runs his phone dark at night expects the app to follow.
//
// Chakra only stores 'light' or 'dark', so "System" is represented by clearing
// the stored key and letting the initialColorMode ('system') resolve again.
// That is why this component tracks its own `choice` alongside Chakra's mode.

import { useEffect, useState } from 'react'
import { HStack, VStack, Box, Text, Icon, useColorMode } from '@chakra-ui/react'
import { Sun, Moon, SunMoon } from 'lucide-react'
import SectionCard from './SectionCard.jsx'

const STORAGE_KEY = 'chakra-ui-color-mode'

const OPTIONS = [
  { value: 'light', label: 'Light', icon: Sun, hint: 'Best on the floor and outdoors' },
  { value: 'dark', label: 'Dark', icon: Moon, hint: 'Easier at a desk and at night' },
  { value: 'system', label: 'System', icon: SunMoon, hint: 'Follows your device' },
]

function readChoice() {
  try {
    return localStorage.getItem(STORAGE_KEY) || 'system'
  } catch {
    return 'system'
  }
}

function prefersDark() {
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches
  } catch {
    return false
  }
}

export default function AppearanceSection() {
  const { colorMode, setColorMode } = useColorMode()
  const [choice, setChoice] = useState(readChoice)

  // When the user is on System, track the device so the app changes with it
  // mid-session rather than only on reload.
  useEffect(() => {
    if (choice !== 'system') return undefined
    let mq
    try {
      mq = window.matchMedia('(prefers-color-scheme: dark)')
    } catch {
      return undefined
    }
    const apply = () => setColorMode(mq.matches ? 'dark' : 'light')
    apply()
    mq.addEventListener('change', apply)
    return () => mq.removeEventListener('change', apply)
  }, [choice, setColorMode])

  function pick(value) {
    setChoice(value)
    if (value === 'system') {
      // Clear the stored preference so nothing overrides the device next load.
      try {
        localStorage.removeItem(STORAGE_KEY)
      } catch {
        /* private mode: the choice is session-only, which is acceptable */
      }
      setColorMode(prefersDark() ? 'dark' : 'light')
      return
    }
    setColorMode(value)
  }

  return (
    <SectionCard
      title="Appearance"
      description="How the Academy looks on this device. Light reads better in a bright showroom; dark is easier on a desk at night."
    >
      <HStack spacing={3} align="stretch" flexWrap={{ base: 'wrap', sm: 'nowrap' }}>
        {OPTIONS.map((opt) => {
          const active = choice === opt.value
          return (
            <Box
              key={opt.value}
              as="button"
              type="button"
              onClick={() => pick(opt.value)}
              aria-pressed={active}
              flex="1"
              minW={{ base: '100%', sm: 0 }}
              textAlign="left"
              borderRadius="input"
              border="1px solid"
              borderColor={active ? 'ink' : 'line'}
              bg={active ? 'surface' : 'transparent'}
              px={4}
              py={3.5}
              transition="border-color 150ms ease, background 150ms ease"
              _hover={{ borderColor: active ? 'ink' : 'lineStrong' }}
            >
              <VStack align="stretch" spacing={1.5}>
                <HStack spacing={2}>
                  <Icon as={opt.icon} boxSize={4} color={active ? 'ink' : 'inkMuted'} />
                  <Text fontSize="body-sm" fontWeight={600} color="ink">
                    {opt.label}
                  </Text>
                </HStack>
                <Text fontSize="xs" color="inkDim" lineHeight={1.5}>
                  {opt.hint}
                </Text>
              </VStack>
            </Box>
          )
        })}
      </HStack>

      <Text textStyle="eyebrowMuted">
        Currently showing {colorMode}
        {choice === 'system' ? ' from your device' : ''}
      </Text>
    </SectionCard>
  )
}
